import Vue from 'vue'
import VueI18n from 'vue-i18n'
import Cookies from 'js-cookie'
import enLocale from './en_US'
import cnLocale from './zh_CN'
import hantLocale from './zh_Hant'
import locale from 'element-ui/lib/locale';
import en from 'element-ui/lib/locale/lang/en';
import zh from 'element-ui/lib/locale/lang/zh-CN';
import zh_hant from 'element-ui/lib/locale/lang/zh-TW';
//需要其它语种可以参考elementui官网地址
//https://element.eleme.cn/#/zh-CN/component/i18n

Vue.use(VueI18n);

export const LANGUAGE_COOKIE = 'language'
export const SUPPORTED_LOCALES = ['en_US', 'zh_CN', 'zh_Hant']

const DEFAULT_LOCALE = 'en_US'
const IP_DETECT_TIMEOUT = 3000

export const LOCALE_NATIVE_NAMES = {
  en_US: 'English',
  zh_CN: '中文简体',
  zh_Hant: '中文繁體'
}

export function getLocaleNativeName(locale) {
  return LOCALE_NATIVE_NAMES[locale] || locale
}

const COUNTRY_LOCALE_MAP = {
  CN: 'zh_CN',
  TW: 'zh_Hant',
  HK: 'zh_Hant',
  MO: 'zh_Hant'
}

const HTML_LANG_MAP = {
  en_US: 'en-US',
  zh_CN: 'zh-CN',
  zh_Hant: 'zh-Hant'
}

const messages = {
  en_US: {
    ...enLocale,
    ...en
  },
  zh_CN: {
    ...cnLocale,
    ...zh
  },
  zh_Hant: {
    ...hantLocale,
    ...zh_hant
  }
}

function isBrowser() {
  return typeof window !== 'undefined'
}

function getNavigator() {
  return isBrowser() ? window.navigator : {}
}

function isSupportedLocale(locale) {
  return SUPPORTED_LOCALES.indexOf(locale) > -1
}

function normalizeBrowserLocale(locale) {
  if (!locale) return ''
  return String(locale).replace(/_/g, '-')
}

function toAppLocale(locale) {
  const normalized = normalizeBrowserLocale(locale)
  const lowerLocale = normalized.toLowerCase()
  if (lowerLocale === 'zh-cn' || lowerLocale === 'zh-sg') return 'zh_CN'
  if (lowerLocale === 'zh-tw' || lowerLocale === 'zh-hk' || lowerLocale === 'zh-mo') return 'zh_Hant'
  if (lowerLocale === 'zh-hant' || lowerLocale.indexOf('zh-hant-') === 0) return 'zh_Hant'
  if (lowerLocale.indexOf('zh-') === 0 || lowerLocale === 'zh') return 'zh_CN'
  if (lowerLocale.indexOf('en-') === 0 || lowerLocale === 'en') return 'en_US'
  if (isSupportedLocale(locale)) return locale
  return 'en_US'
}

function getBrowserLocale() {
  const nav = getNavigator()
  const languages = nav.languages && nav.languages.length ? nav.languages : [nav.language || nav.userLanguage]
  for (let i = 0; i < languages.length; i++) {
    const locale = toAppLocale(languages[i])
    if (isSupportedLocale(locale)) return locale
  }
  return DEFAULT_LOCALE
}

function setDocumentLang(locale) {
  if (!isBrowser() || !window.document) return
  window.document.documentElement.setAttribute('lang', HTML_LANG_MAP[locale] || locale)
}

function isAnonymousNetwork(geo) {
  return geo.is_vpn || geo.is_proxy || geo.is_tor || geo.is_datacenter
}

function getLocaleByCountry(countryCode) {
  if (!countryCode) return ''
  const code = String(countryCode).toUpperCase()
  return COUNTRY_LOCALE_MAP[code] || 'en_US'
}

// 获取 cookie 中已保存的语言（存在即表示用户已确认）
function getSavedLocale() {
  const saved = Cookies.get(LANGUAGE_COOKIE)
  return isSupportedLocale(saved) ? saved : null
}

// 写入 cookie（初始化写入 + 更新写入共用）
function writeCookie(locale) {
  if (!isSupportedLocale(locale)) return false
  Cookies.set(LANGUAGE_COOKIE, locale, { expires: 365 })
  return true
}

// 创建 VueI18n 实例（初始 locale 后续会被 initializeLocale 覆盖）
const i18n = new VueI18n({
  locale: getSavedLocale() || getBrowserLocale(),
  messages
})
locale.i18n((key, value) => i18n.t(key, value))
setDocumentLang(i18n.locale)

/**
 * 设置语言偏好（用于手动切换和弹窗确认 — 更新写入）
 */
export function setLocalePreference(locale) {
  if (!isSupportedLocale(locale)) return false
  writeCookie(locale)
  i18n.locale = locale
  setDocumentLang(locale)
  return true
}

/**
 * 初始化语言（应用启动时调用，阻塞在 Vue 挂载前）
 *
 * 优先级：cookie > IP地理检测(3s超时) > 浏览器语言 > 兜底英文
 *
 * @param {Object} options
 * @param {Function} options.detectGeo - 返回地理信息的异步函数
 * @param {boolean} options.respectPrivacy - 是否跳过匿名网络
 * @param {Function} options.confirmLocaleChange - 弹窗确认函数（仅异步差异场景）
 * @param {Function} options.onLocaleChange - 语言变更后的回调
 * @param {number} options.timeout - IP检测超时时间(ms)，默认3000
 */
export async function initializeLocale(options = {}) {
  // 优先级 1：cookie 存在 → 直接使用，不做检测
  const saved = getSavedLocale()
  if (saved) {
    i18n.locale = saved
    setDocumentLang(saved)
    return { source: 'cookie', locale: saved }
  }

  // 无 cookie → 需要检测
  const { detectGeo, respectPrivacy, confirmLocaleChange, onLocaleChange } = options
  const timeout = options.timeout || IP_DETECT_TIMEOUT

  // 无地理检测器 → 直接用浏览器语言
  if (typeof detectGeo !== 'function') {
    const locale = getBrowserLocale()
    writeCookie(locale)
    i18n.locale = locale
    setDocumentLang(locale)
    return { source: 'browser', locale }
  }

  // 优先级 2：IP 同步检测（带超时）
  const geoPromise = detectGeo().catch(() => null)
  const raceResult = await Promise.race([
    geoPromise.then(geo => ({ timedOut: false, geo })),
    new Promise(resolve => setTimeout(() => resolve({ timedOut: true }), timeout))
  ])

  if (!raceResult.timedOut) {
    const geo = raceResult.geo
    if (geo && (respectPrivacy === false || !isAnonymousNetwork(geo))) {
      const ipLocale = getLocaleByCountry(geo.country_code)
      if (isSupportedLocale(ipLocale)) {
        // IP 同步命中 → 直接用，初始化写入 cookie
        writeCookie(ipLocale)
        i18n.locale = ipLocale
        setDocumentLang(ipLocale)
        return { source: 'ip_sync', locale: ipLocale, geo }
      }
    }
    // IP 返回但无法使用 → 降级到浏览器语言
    const locale = getBrowserLocale()
    writeCookie(locale)
    i18n.locale = locale
    setDocumentLang(locale)
    return { source: 'browser', locale }
  }

  // 优先级 3 & 4：IP 超时 → 浏览器语言 / 兜底英文，初始化写入 cookie
  const browserLocale = getBrowserLocale()
  writeCookie(browserLocale)
  i18n.locale = browserLocale
  setDocumentLang(browserLocale)

  // 异步继续等待 IP 结果
  geoPromise.then(geo => {
    if (!geo) return
    if (respectPrivacy !== false && isAnonymousNetwork(geo)) return
    const ipLocale = getLocaleByCountry(geo.country_code)
    if (!isSupportedLocale(ipLocale)) return
    if (ipLocale === i18n.locale) return

    // IP 结果与当前语言不同 → 弹窗确认
    if (typeof confirmLocaleChange === 'function') {
      confirmLocaleChange({ currentLocale: i18n.locale, targetLocale: ipLocale, geo })
        .then(confirmed => {
          if (confirmed) {
            // 用户确认 → 更新写入 cookie
            setLocalePreference(ipLocale)
            if (typeof onLocaleChange === 'function') onLocaleChange(ipLocale)
          }
          // 用户拒绝 → 不操作（cookie 保持初始化值）
        })
    }
  })

  return { source: 'browser', locale: browserLocale, asyncPending: true }
}

export default i18n
